import { useState, ReactNode, useContext } from 'react'
import { Typography, ButtonBase, styled } from '@mui/material'
import CircularProgress, {
  circularProgressClasses,
} from '@mui/material/CircularProgress'
import { useTimeout } from '$hooks/use-timeout'
import { showErrorSnackbar } from '$comp/snackbars'
import { t } from '$i18n'
import { PvControlsContext } from '$comp/pv-call-manager-page'

const ACTION_TIMEOUT = 5000

const Container = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;

  width: fit-content;
  transition: opacity 200ms linear;
`

const Loading = styled(CircularProgress)`
  color: #fff;

  & .${circularProgressClasses.circle} {
    stroke-linecap: round;
  }
`

const Button = styled(ButtonBase)({
  width: 56,
  height: 56,
  borderRadius: '50%',

  '& > svg': {
    width: 24,
    height: 24,
    color: '#fff',
  },
})

interface Props {
  caption: string
  disabled?: boolean
  icon: ReactNode
  color?: 'error' | 'success' | 'gray'
  onClick: () => Promise<void> | void
  onActionTimeout?: (timeOutMS: number) => void
}

export function ControlButton({
  caption,
  disabled = false,
  icon,
  color = 'success',
  onClick,
  onActionTimeout = () => showErrorSnackbar(t('snackbar.call.requestFailed')),
}: Props) {
  const [loading, setLoading] = useState(false)
  const { create, clear } = useTimeout()
  const { performing, setPerforming } = useContext(PvControlsContext)

  const performAction = async () => {
    if (loading) return

    clear()
    setLoading(true)
    setPerforming(true)

    try {
      create(() => {
        setLoading(false)
        setPerforming(false)
        onActionTimeout(ACTION_TIMEOUT)
      }, ACTION_TIMEOUT)

      await onClick()
    } finally {
      clear()
      setLoading(false)
      setPerforming(false)
    }
  }

  const shouldBeDisabled = disabled || (!loading && performing)
  return (
    <Container sx={{ opacity: shouldBeDisabled ? 0.5 : 1 }}>
      <Button
        disabled={shouldBeDisabled}
        sx={{
          bgcolor: theme =>
            `${
              color === 'gray' ? theme.palette.gray : theme.palette[color].main
            } !important`,
        }}
        onClick={performAction}
      >
        {loading ? <Loading size={20} thickness={3} /> : icon}
      </Button>

      <Typography variant="body2" color="white" mt={1}>
        {caption}
      </Typography>
    </Container>
  )
}
